import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { capitalizeAllWords } from '../../utils'
import Button from '../Button'
import Modal from '../Modal'

import styles from './confirmationModal.module.scss'

interface ConfirmationModalProps {
  open: boolean
  onClose: () => void
  onConfirm?: () => Promise<void>
  onPrimaryAction?: () => void
  message: string
  title?: string
  primaryButtonText?: string
  formSubmitted?: boolean
  isLoading?: boolean
  buttonLayout?: 'horizontal' | 'vertical'
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  onPrimaryAction,
  message,
  title,
  primaryButtonText,
  formSubmitted = false,
  buttonLayout = 'horizontal',
  isLoading = false,
}) => {
  const { t } = useTranslation('accountPage')
  const displayTitle = title ?? capitalizeAllWords(t('deletePayment'))
  const displayPrimaryText = primaryButtonText ?? capitalizeAllWords(t('delete'))
  const handlePrimaryClick =
    onPrimaryAction ??
    (onConfirm
      ? async () => {
          await onConfirm()
        }
      : () => undefined)

  const handleClose = isLoading ? () => undefined : onClose

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={styles.styledModal}
      closePosition={16}
      // eslint-disable-next-line i18next/no-literal-string
      closeButtonTestId="confirmation-modal-close"
      closeOnBGClick={!isLoading}
    >
      <div className={classnames(styles.modalTitle, 'subtitle1_grosa')} data-testid="confirmation-modal-title">
        {displayTitle}
      </div>
      <div className={classnames(styles.modalMessage, 'body1_grosa')} data-testid="confirmation-modal-message">
        {message}
      </div>
      <div
        className={classnames(styles.buttonContainer, buttonLayout === 'vertical' && styles.verticalLayout)}
        data-testid="confirmation-modal-buttons"
      >
        {/* @ts-expect-error - Button component from JS file lacks proper TypeScript types */}
        <Button
          onClick={handlePrimaryClick}
          disabled={formSubmitted || isLoading}
          loading={isLoading}
          className={classnames(
            styles.styledPrimaryButton,
            primaryButtonText ? styles.styledManageButton : styles.styledDeleteButton,
            'button_primary',
          )}
          data-testid="confirmation-modal-primary-button"
        >
          {displayPrimaryText}
        </Button>
        {/* @ts-expect-error - Button component from JS file lacks proper TypeScript types */}
        <Button
          onClick={onClose}
          disabled={formSubmitted || isLoading}
          className={classnames(styles.styledCancelButton, 'button_secondary')}
          data-testid="confirmation-modal-cancel-button"
        >
          {capitalizeAllWords(t('cancel'))}
        </Button>
      </div>
    </Modal>
  )
}

export default ConfirmationModal
